import { CommonModule } from '@angular/common'
import {
  Component,
  computed,
  DestroyRef,
  ElementRef,
  HostListener,
  signal,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { RouterModule } from '@angular/router'
import {
  OcDividerComponent,
  OcDropdownMenuItemComponent,
  OcDropdownMenuLabelComponent,
} from 'otimus-library'
import { catchError, debounceTime, distinctUntilChanged, of, Subject, switchMap } from 'rxjs'
import { MenuSearchUser } from 'src/app/components/sidebar/entities/menu-search-response.entity'
import { FlatNavigationItem } from 'src/app/components/sidebar/entities/flat-navigation-item.entity'
import { ProfilePicComponent } from 'src/app/components/profile-pic/profile-pic.component'
import { SidebarService } from 'src/app/services/sidebar.service'
import { InternationalizationService } from 'src/app/services/internationalization.service'
import { MessageChatStateService } from 'src/app/modules/message-chat/services/message-chat-state.service'

const MIN_QUERY_LENGTH = 2
const MENU_RESULT_LIMIT = 20

@Component({
  selector: 'app-search',
  imports: [
    CommonModule,
    RouterModule,
    OcDropdownMenuItemComponent,
    OcDropdownMenuLabelComponent,
    OcDividerComponent,
    ProfilePicComponent,
  ],
  templateUrl: './search.component.html',
  styleUrl: './search.component.scss',
})
export class SearchComponent {
  private readonly query$ = new Subject<string>()

  protected readonly query = signal('')
  protected readonly focused = signal(false)
  protected readonly loadingUsers = signal(false)
  protected readonly users = signal<MenuSearchUser[]>([])

  // Menu search is done locally against translated flat items — no backend call.
  protected readonly menus = computed<FlatNavigationItem[]>(() => {
    const term = this.query().trim().toLowerCase()
    if (term.length < MIN_QUERY_LENGTH) return []
    return this.sidebarService
      .flatMenuItems()
      .filter((item) => item.label.toLowerCase().includes(term))
      .slice(0, MENU_RESULT_LIMIT)
  })

  protected readonly loading = computed(() => this.loadingUsers())

  protected readonly isOpen = computed(
    () => this.focused() && this.query().trim().length >= MIN_QUERY_LENGTH
  )
  protected readonly hasResults = computed(
    () => this.menus().length > 0 || this.users().length > 0
  )

  constructor(
    private readonly sidebarService: SidebarService,
    private readonly chatState: MessageChatStateService,
    protected readonly translateService: InternationalizationService,
    private readonly host: ElementRef<HTMLElement>,
    private readonly destroyRef: DestroyRef,
  ) {
    this.query$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term) => {
          if (term.trim().length < MIN_QUERY_LENGTH) {
            this.loadingUsers.set(false)
            return of({ menus: [], users: [] })
          }
          this.loadingUsers.set(true)
          return this.sidebarService.searchMenu(term).pipe(
            catchError(() => of({ data: { menus: [], users: [] } })),
            switchMap((res) => of(('data' in res ? res.data : res) as { users: MenuSearchUser[] }))
          )
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((result) => {
        this.users.set(result.users ?? [])
        this.loadingUsers.set(false)
      })
  }

  protected onInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value
    this.query.set(value)
    this.query$.next(value)
  }

  protected onFocus(): void {
    this.focused.set(true)
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.clear()
      ;(event.target as HTMLInputElement).blur()
    }
  }

  protected selectUser(user: MenuSearchUser): void {
    this.chatState.pendingOpenUser.set({
      userId: user.userId,
      userName: user.personName || user.name,
    })
    this.clear()
  }

  protected clear(): void {
    this.query.set('')
    this.users.set([])
    this.focused.set(false)
  }

  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    if (!this.host.nativeElement.contains(event.target as Node)) {
      this.focused.set(false)
    }
  }
}
